import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Utils from "../../utils";
import { clientActions } from "../../actions/client.actions";
import { clientService } from "../../services/client.service";
import Title from "../common/Title";
import { GoOrganization } from "react-icons/go";
import SkeletonForm from "../common/SkeletonForm";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import { useToast } from "../../contexts/ToastContext";

function ClientForm({ presetClientId, onSaveSuccess, onCancel, hideTitle }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [clientId, setClientId] = useState("");
  const [clientGuid, setClientGuid] = useState("");
  const [clientName, setClientName] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loadingClient, setLoadingClient] = useState(false);
  const clientReducer = useSelector((state) => state.client);

  useEffect(() => {
    let queryParams = Utils.getQueryStringParams(location);
    const clientIdToLoad = presetClientId ?? queryParams["clientId"];
    if (clientIdToLoad) {
      setClientId(clientIdToLoad);
      setLoadingClient(true);
      // Fetched directly rather than through clientActions.get/CALLER_CLIENT_GRID — that
      // dispatch writes into the same state.client.paginatedResultGrid the Clients list
      // (ClientGrid) reads from, so prefilling this form while the list is open behind it
      // clobbered the list down to just this one client, and it stayed that way even after
      // this form closed since nothing re-fetches the list on close.
      //
      // Only clientId is sent (not the URL's grid params — search text, paging, sort) and the
      // result is matched on clientId exactly: the server's clientId filter is a search, so
      // taking data[0] could return a different client and renew the wrong clientGuid.
      clientService.get({ clientId: clientIdToLoad, status: "all", pageSize: 100 }).then(
        (response) => {
          const found = (response.data?.data ?? []).find(
            (c) => String(c.clientId).toLowerCase() === String(clientIdToLoad).toLowerCase()
          );
          if (found) {
            setClientGuid(found.clientGuid);
            setClientName(found.clientName);
            setExpiresOn(Utils.formatDateToInput(found.expiresOn));
          }
          setLoadingClient(false);
        },
        () => setLoadingClient(false)
      );
    } else {
      setClientId("");
      setClientGuid("");
      setClientName("");
      setExpiresOn("");
    }
    // eslint-disable-next-line
  }, [location, presetClientId]);

  // Gated on this instance's own `submitted` flag, not just clientReducer.success: that flag
  // lives in the global client slice and outlives this form (nothing resets it on close), so a
  // freshly-mounted form (e.g. reopening "Renew" for another client right after a successful
  // one) would otherwise see the previous renewal's stale `success: true` on mount and
  // immediately toast + close before the user ever saw the date field.
  useEffect(() => {
    if (submitted && clientReducer.success) {
      showToast("Client saved successfully", "success");
      onSaveSuccess ? onSaveSuccess() : navigate("/client");
    }
    // eslint-disable-next-line
  }, [clientReducer.success]);

  const handleSaveClient = () => {
    setSubmitted(true);

    if (clientName && expiresOn) {
      if (clientId) {
        dispatch(
          clientActions.renew({
            clientId,
            clientGuid,
            expiresOn,
          })
        );
      } else {
        dispatch(
          clientActions.create({
            clientName,
            expiresOn,
          })
        );
      }
    }
  };

  const operation = !clientId ? "Add" : "Renew";

  return (
    <>
      {!hideTitle && (
        <Title
          text={`${operation} Client`}
          icon={<GoOrganization className="mb-1" />}
        />
      )}

      {loadingClient && <SkeletonForm fields={4} />}

      {!loadingClient && (
        <>
          {!clientReducer.success && (
            <>
              {clientReducer.error && (
                <Alert variant={"danger"}>
                  An error has occurred. Please try again later.
                </Alert>
              )}

              {clientId && (
                <Form.Group controlId="formClientId" className="mb-3">
                  <Form.Label>Client Id</Form.Label>
                  <Form.Control
                    type="text"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    disabled
                  />
                </Form.Group>
              )}

              {!clientId && (
                <Form.Group controlId="formClientName" className="mb-3">
                  <Form.Label>Client Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    className={submitted && !clientName ? "is-invalid" : ""}
                  />
                  <Form.Control.Feedback type="invalid">
                    <small>Please provide a client name.</small>
                  </Form.Control.Feedback>
                </Form.Group>
              )}

              <Form.Group controlId="formExpiresOn" className="mb-3">
                <Form.Label>Expires On</Form.Label>
                <Form.Control
                  type="date"
                  value={expiresOn}
                  onChange={(e) => setExpiresOn(e.target.value)}
                  className={submitted && !expiresOn ? "is-invalid" : ""}
                />
                <Form.Control.Feedback type="invalid">
                  <small>Please provide an expiration date.</small>
                </Form.Control.Feedback>
              </Form.Group>

              <Button
                variant="outline-secondary"
                type="button"
                className="me-3 mt-3"
                onClick={() => (onCancel ? onCancel() : navigate(-1))}
              >
                Cancel
              </Button>

              <Button
                variant="primary"
                type="submit"
                className="mt-3"
                onClick={handleSaveClient}
                disabled={clientReducer.loadingGrid}
              >
                {!clientReducer.loadingGrid ? (
                  <>{operation} Client</>
                ) : (
                  <Spinner size="sm" />
                )}
              </Button>
            </>
          )}
        </>
      )}
    </>
  );
}

export default ClientForm;
